import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, ChevronRight, Tag, Store } from 'lucide-react';
import { SEO } from '../components/common/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { DealCard } from '../components/common/DealCard';
import { cities, getAreasForCity, categories } from '../data/constants';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Helper to format slug to display name
const slugToName = (slug) => {
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Helper to create slug from name
const nameToSlug = (name) => {
  return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
};

export const CityDealsPage = () => {
  const location = window.location.pathname;
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });

  // Parse city from URL: "/pune-deals" -> "Pune"
  const pathMatch = location.match(/\/([a-z]+)-deals/);
  const cityName = pathMatch ? slugToName(pathMatch[1]) : '';
  const matchedCity = cities.find(c => c.toLowerCase() === cityName.toLowerCase());
  const areas = matchedCity ? getAreasForCity(matchedCity) : [];
  const topAreas = areas.slice(0, 10); // Show top 10 areas

  // Popular categories for SEO
  const popularCategories = [
    { name: 'Food & Dining', slug: 'food-dining', icon: '🍽️' },
    { name: 'Fashion', slug: 'fashion', icon: '👗' },
    { name: 'Electronics', slug: 'electronics', icon: '📱' },
    { name: 'Groceries', slug: 'groceries', icon: '🛒' },
    { name: 'Health & Beauty', slug: 'health-beauty', icon: '💄' },
    { name: 'Services', slug: 'services', icon: '🔧' },
  ];

  useEffect(() => {
    const fetchDeals = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/deals`, {
          params: { city: matchedCity || cityName, limit: 20, page: 1 }
        });
        setDeals(res.data.deals || []);
        setPagination(res.data.pagination || { total: 0 });
      } catch (err) {
        console.error('Error fetching deals:', err);
      }
      setLoading(false);
    };
    fetchDeals();
  }, [cityName, matchedCity]);

  const seoTitle = `Best Deals & Offers in ${cityName} | Local Discounts | JustOffs`;
  const seoDescription = `Discover ${pagination.total}+ best deals in ${cityName}. Find local deals, discounts near me, combo offers & flash sales from verified shops. Food, fashion, electronics & more. Updated daily!`;

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO title={seoTitle} description={seoDescription} />
      <Header />

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="text-sm text-slate-500 mb-6 flex items-center gap-2">
          <Link to="/" className="hover:text-orange-500">Home</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-slate-800 font-medium">{cityName} Deals</span>
        </nav>

        {/* Hero Section - Compact */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 md:p-8 mb-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
              <MapPin className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">Best Deals in {cityName}</h1>
              <p className="text-slate-400">{pagination.total} offers available</p>
            </div>
          </div>
          <p className="text-slate-300 text-sm md:text-base leading-relaxed max-w-2xl">
            Discover the best local deals and discounts near you in {cityName}. Save on food, fashion, electronics & more.
          </p>
        </div>

        {/* Deals Grid - Primary Content */}
        <section className="mb-10">
          <h2 className="text-lg font-bold text-slate-800 mb-4">Latest Deals in {cityName}</h2>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl h-64 animate-pulse" />
              ))}
            </div>
          ) : deals.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {deals.map(deal => (
                <DealCard key={deal.id} deal={deal} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-xl">
              <Store className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No deals found in {cityName} yet.</p>
              <Link to="/post-ad" className="text-orange-500 font-medium hover:underline mt-2 inline-block">
                Be the first to post a deal!
              </Link>
            </div>
          )}
        </section>

        {/* View All Link */}
        {deals.length > 0 && (
          <div className="text-center mb-12">
            <Link
              to={`/deals?city=${encodeURIComponent(matchedCity || cityName)}`}
              className="inline-flex items-center gap-2 bg-orange-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-orange-600 transition"
            >
              View All {pagination.total} Deals <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {/* Popular Categories in City */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-slate-800 mb-4">Popular Categories in {cityName}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {popularCategories.map(cat => (
              <Link
                key={cat.slug}
                to={`/${cat.slug}-deals-${nameToSlug(cityName)}`}
                className="bg-white border border-slate-200 rounded-xl p-4 text-center hover:border-orange-400 hover:shadow-md transition group"
              >
                <span className="text-2xl mb-2 block">{cat.icon}</span>
                <span className="text-sm font-medium text-slate-700 group-hover:text-orange-600">{cat.name}</span>
              </Link>
            ))}
          </div>
        </section>

        {/* Popular Areas in City */}
        {topAreas.length > 0 && (
          <section className="mb-10">
            <h2 className="text-xl font-bold text-slate-800 mb-4">Popular Areas in {cityName}</h2>
            <div className="flex flex-wrap gap-2">
              {topAreas.map(area => (
                <Link
                  key={area.id}
                  to={`/${nameToSlug(cityName)}/${nameToSlug(area.name)}-deals`}
                  className="bg-white border border-slate-200 px-4 py-2.5 rounded-lg text-sm text-slate-700 hover:border-orange-400 hover:text-orange-600 transition flex items-center gap-2"
                >
                  <MapPin className="w-3 h-3" /> {area.name}
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Extended SEO Content - Below Listings */}
        <section className="bg-white rounded-2xl p-6 md:p-8 mb-8">
          <h2 className="text-xl font-bold text-slate-800 mb-5">Best Deals & Discounts Near Me in {cityName}</h2>
          <div className="text-slate-600 space-y-4 leading-relaxed">
            <p>
              Looking for the <strong>best deals in {cityName}</strong>? You've come to the right place. JustOffs is {cityName}'s 
              leading platform for discovering exclusive <strong>offers in {cityName}</strong> from verified local shopkeepers and businesses. 
              Whether you're searching for <strong>discounts near me in {cityName}</strong>, exploring neighborhood shops, or hunting for 
              the biggest savings in town, our platform connects you directly with merchants offering authentic deals on everything 
              from daily essentials to premium products. With hundreds of <strong>local deals {cityName}</strong> updated every day, 
              you'll never miss out on savings again.
            </p>
            
            <h3 className="text-lg font-semibold text-slate-800 pt-2">Shop by Category</h3>
            <p>
              {cityName} offers a vibrant and diverse shopping experience across multiple categories. Discover incredible{' '}
              <Link to={`/food-dining-deals-${nameToSlug(cityName)}`} className="text-orange-500 hover:underline">Food & Dining deals</Link> — 
              from budget-friendly street food to fine dining restaurants offering combo meals, happy hour specials, and exclusive 
              dine-in discounts. Fashion lovers can explore{' '}
              <Link to={`/fashion-deals-${nameToSlug(cityName)}`} className="text-orange-500 hover:underline">Fashion offers in {cityName}</Link> featuring 
              flat discounts up to 70% off, buy-one-get-one deals, and seasonal clearance sales from leading clothing brands and local boutiques. 
              Tech enthusiasts will find amazing{' '}
              <Link to={`/electronics-deals-${nameToSlug(cityName)}`} className="text-orange-500 hover:underline">Electronics discounts</Link> on 
              smartphones, laptops, TVs, and accessories with exchange bonuses, no-cost EMI, and extended warranties. Don't forget to check out 
              <strong> grocery deals</strong>, <strong>health & beauty offers</strong>, and <strong>home services discounts</strong> to maximize your savings.
            </p>

            <h3 className="text-lg font-semibold text-slate-800 pt-2">Explore Popular Shopping Areas</h3>
            <p>
              {cityName} boasts numerous shopping destinations, each offering its own unique charm and deals. 
              {topAreas.length > 0 && (
                <>
                  {' '}Popular areas like{' '}
                  {topAreas.slice(0, 4).map((area, idx) => (
                    <span key={area.id}>
                      <Link to={`/${nameToSlug(cityName)}/${nameToSlug(area.name)}-deals`} className="text-orange-500 hover:underline">
                        {area.name}
                      </Link>
                      {idx < 3 ? ', ' : ' '}
                    </span>
                  ))}
                  and more are packed with shops offering exclusive <strong>discounts near me in {cityName}</strong>.
                </>
              )}
              {' '}Whether you prefer the energy of street markets, the convenience of shopping malls, or the personalized service of 
              neighborhood stores, you'll find <strong>best deals in {cityName}</strong> wherever you go. Our GPS-powered "Near Me" feature 
              helps you discover <strong>offers in {cityName}</strong> within walking distance, so you can shop local and save big.
            </p>

            <h3 className="text-lg font-semibold text-slate-800 pt-2">Types of Deals You'll Find</h3>
            <p>
              Our platform features a wide variety of <strong>local deals {cityName}</strong> shoppers love:
            </p>
            <ul className="list-disc list-inside space-y-1 text-slate-600 ml-2">
              <li><strong>Flat Discounts</strong> — Up to 50-70% off on products and services</li>
              <li><strong>Combo Offers</strong> — Bundle deals that give you more value for money</li>
              <li><strong>Flash Sales</strong> — Limited-time deals with the deepest discounts</li>
              <li><strong>Clearance Sales</strong> — End-of-season and stock clearance at rock-bottom prices</li>
              <li><strong>Buy One Get One Free</strong> — Double the value on popular items</li>
              <li><strong>New Store Offers</strong> — Exclusive launch discounts from new businesses</li>
              <li><strong>Festive Deals</strong> — Special offers during Diwali, Eid, Christmas, and more</li>
            </ul>

            <h3 className="text-lg font-semibold text-slate-800 pt-2">Why Thousands Trust JustOffs for {cityName} Deals</h3>
            <p>
              JustOffs has become the go-to destination for <strong>best deals in {cityName}</strong> because we prioritize authenticity, 
              convenience, and direct connections. Every deal on our platform is posted by verified shopkeepers, so you'll never 
              encounter expired coupons or fake discounts. Our WhatsApp integration lets you chat directly with sellers to confirm 
              availability, ask questions, and even negotiate prices — no middlemen, no hidden fees.
            </p>
            <p>
              The platform is <strong>100% free</strong> for both shopkeepers and buyers. Sellers can post unlimited deals in seconds, 
              while shoppers can browse hundreds of <strong>offers in {cityName}</strong> updated in real-time. Our location-based discovery 
              makes finding <strong>discounts near me in {cityName}</strong> effortless — just enable GPS and see what's around you. 
              Join thousands of smart shoppers who save money every day with JustOffs!
            </p>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-6 md:p-8 mb-8 text-center">
          <h3 className="text-xl md:text-2xl font-bold text-white mb-3">Are You a Shopkeeper in {cityName}?</h3>
          <p className="text-orange-100 mb-5 max-w-xl mx-auto">
            Reach thousands of local customers looking for deals. Post your offers for FREE and grow your business today!
          </p>
          <Link 
            to="/post-ad" 
            className="inline-flex items-center gap-2 bg-white text-orange-600 px-6 py-3 rounded-xl font-bold hover:bg-orange-50 transition shadow-lg"
          >
            Post Your Deal FREE in {cityName} <ChevronRight className="w-5 h-5" />
          </Link>
        </section>

        {/* Quick Links */}
        <section className="bg-slate-100 rounded-2xl p-6">
          <h3 className="font-semibold text-slate-800 mb-3">Quick Links</h3>
          <div className="flex flex-wrap gap-2 text-sm">
            <Link to="/deals" className="text-orange-500 hover:underline">All Deals</Link>
            <span className="text-slate-400">•</span>
            <Link to="/post-ad" className="text-orange-500 hover:underline">Post Free Ad</Link>
            <span className="text-slate-400">•</span>
            <Link to={`/deals?city=${encodeURIComponent(matchedCity || cityName)}&offer_type=Combo%20Offer`} className="text-orange-500 hover:underline">Combo Offers in {cityName}</Link>
            <span className="text-slate-400">•</span>
            <Link to={`/deals?city=${encodeURIComponent(matchedCity || cityName)}&offer_type=Flash%20Sale`} className="text-orange-500 hover:underline">Flash Sales in {cityName}</Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export const AreaDealsPage = () => {
  const { areaSlug } = useParams();
  const location = window.location.pathname;
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0 });

  // Parse city from path: "/pune/kharadi-deals" -> "Pune"
  const pathMatch = location.match(/\/([a-z]+)\/([a-z-]+)/);
  const cityName = pathMatch ? slugToName(pathMatch[1]) : '';
  const areaName = slugToName(areaSlug?.replace(/-deals$/, '') || '');
  const matchedCity = cities.find(c => c.toLowerCase() === cityName.toLowerCase());

  useEffect(() => {
    const fetchDeals = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/deals`, {
          params: { city: matchedCity || cityName, area: areaName, limit: 20, page: 1 }
        });
        setDeals(res.data.deals || []);
        setPagination(res.data.pagination || { total: 0 });
      } catch (err) {
        console.error('Error fetching deals:', err);
      }
      setLoading(false);
    };
    fetchDeals();
  }, [cityName, areaName, matchedCity]);

  const seoTitle = `Deals in ${areaName}, ${cityName} | Local Offers | JustOffs`;
  const seoDescription = `Find ${pagination.total}+ deals and offers in ${areaName}, ${cityName}. Discover discounts from local shops, restaurants, and stores near you. Save big today!`;

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO title={seoTitle} description={seoDescription} />
      <Header />

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="text-sm text-slate-500 mb-6 flex items-center gap-2 flex-wrap">
          <Link to="/" className="hover:text-orange-500">Home</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to={`/${nameToSlug(cityName)}-deals`} className="hover:text-orange-500">{cityName}</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-slate-800 font-medium">{areaName}</span>
        </nav>

        {/* Hero Section */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
              <MapPin className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">Deals in {areaName}</h1>
              <p className="text-slate-400">{cityName} • {pagination.total} offers</p>
            </div>
          </div>
          <p className="text-slate-300 leading-relaxed max-w-2xl">
            Discover exclusive deals and discounts from shops in {areaName}, {cityName}. 
            Find the best offers on food, fashion, electronics, and more from verified local businesses.
            Connect directly with shopkeepers and save money on your everyday purchases.
          </p>
        </div>

        {/* Deals Grid */}
        <section>
          <h2 className="text-lg font-bold text-slate-800 mb-4">Available Deals in {areaName}</h2>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl h-64 animate-pulse" />
              ))}
            </div>
          ) : deals.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {deals.map(deal => (
                <DealCard key={deal.id} deal={deal} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-xl">
              <Store className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No deals found in {areaName} yet.</p>
              <Link to="/post-ad" className="text-orange-500 font-medium hover:underline mt-2 inline-block">
                Post the first deal!
              </Link>
            </div>
          )}
        </section>

        {/* Back to City */}
        <div className="text-center mt-8">
          <Link
            to={`/${nameToSlug(cityName)}-deals`}
            className="inline-flex items-center gap-2 text-orange-500 font-medium hover:underline"
          >
            ← View all deals in {cityName}
          </Link>
        </div>

        {/* SEO Content */}
        <section className="mt-12 bg-white rounded-2xl p-6 md:p-8">
          <h2 className="text-xl font-bold text-slate-800 mb-4">Shop Local in {areaName}, {cityName}</h2>
          <div className="text-slate-600 space-y-4 leading-relaxed">
            <p>
              {areaName} is one of the popular shopping destinations in {cityName}. Find amazing deals from 
              local stores, restaurants, and service providers right in your neighborhood.
            </p>
            <p>
              JustOffs connects you with verified shopkeepers offering exclusive discounts. Browse offers 
              by category, compare prices, and connect via WhatsApp for instant queries. Support local 
              businesses while saving money on every purchase.
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export const CategoryDealsPage = () => {
  const { categorySlug, citySlug } = useParams();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0 });

  // Parse: "fashion-deals-pune" → category=fashion, city=pune
  const slugParts = categorySlug?.split('-deals-') || [];
  const categoryName = slugParts[0] ? slugToName(slugParts[0]) : '';
  const cityFromSlug = slugParts[1] ? slugToName(slugParts[1]) : (citySlug ? slugToName(citySlug) : '');
  
  const matchedCategory = categories.find(c => c.name.toLowerCase() === categoryName.toLowerCase());
  const matchedCity = cities.find(c => c.toLowerCase() === cityFromSlug.toLowerCase());

  useEffect(() => {
    const fetchDeals = async () => {
      setLoading(true);
      try {
        const params = { limit: 20, page: 1 };
        if (matchedCategory) params.category = matchedCategory.name;
        if (matchedCity) params.city = matchedCity;
        
        const res = await axios.get(`${API}/api/deals`, { params });
        setDeals(res.data.deals || []);
        setPagination(res.data.pagination || { total: 0 });
      } catch (err) {
        console.error('Error fetching deals:', err);
      }
      setLoading(false);
    };
    fetchDeals();
  }, [categoryName, cityFromSlug, matchedCategory, matchedCity]);

  const displayCategory = matchedCategory?.name || categoryName;
  const displayCity = matchedCity || cityFromSlug;
  const seoTitle = `${displayCategory} Deals in ${displayCity} | JustOffs`;
  const seoDescription = `Find ${pagination.total}+ ${displayCategory.toLowerCase()} deals and offers in ${displayCity}. Exclusive discounts from local ${displayCategory.toLowerCase()} shops. Save big today!`;

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO title={seoTitle} description={seoDescription} />
      <Header />

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="text-sm text-slate-500 mb-6 flex items-center gap-2 flex-wrap">
          <Link to="/" className="hover:text-orange-500">Home</Link>
          <ChevronRight className="w-4 h-4" />
          {displayCity && (
            <>
              <Link to={`/${nameToSlug(displayCity)}-deals`} className="hover:text-orange-500">{displayCity}</Link>
              <ChevronRight className="w-4 h-4" />
            </>
          )}
          <span className="text-slate-800 font-medium">{displayCategory}</span>
        </nav>

        {/* Hero Section */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
              <Tag className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">{displayCategory} Deals</h1>
              <p className="text-slate-400">{displayCity} • {pagination.total} offers</p>
            </div>
          </div>
          <p className="text-slate-300 leading-relaxed max-w-2xl">
            Find the best {displayCategory.toLowerCase()} deals and offers in {displayCity}. 
            Browse exclusive discounts from local {displayCategory.toLowerCase()} stores, compare prices, 
            and connect directly with sellers. Save money on quality {displayCategory.toLowerCase()} products today.
          </p>
        </div>

        {/* Deals Grid */}
        <section>
          <h2 className="text-lg font-bold text-slate-800 mb-4">{displayCategory} Offers in {displayCity}</h2>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl h-64 animate-pulse" />
              ))}
            </div>
          ) : deals.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {deals.map(deal => (
                <DealCard key={deal.id} deal={deal} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-xl">
              <Tag className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No {displayCategory.toLowerCase()} deals found in {displayCity}.</p>
              <Link to="/post-ad" className="text-orange-500 font-medium hover:underline mt-2 inline-block">
                Post a {displayCategory.toLowerCase()} deal!
              </Link>
            </div>
          )}
        </section>

        {/* View All Link */}
        <div className="text-center mt-8">
          <Link
            to={`/${nameToSlug(displayCity)}-deals`}
            className="inline-flex items-center gap-2 text-orange-500 font-medium hover:underline"
          >
            ← View all deals in {displayCity}
          </Link>
        </div>

        {/* SEO Content */}
        <section className="mt-12 bg-white rounded-2xl p-6 md:p-8">
          <h2 className="text-xl font-bold text-slate-800 mb-4">{displayCategory} Shopping in {displayCity}</h2>
          <div className="text-slate-600 space-y-4 leading-relaxed">
            <p>
              Looking for the best {displayCategory.toLowerCase()} deals in {displayCity}? JustOffs brings you 
              verified offers from local {displayCategory.toLowerCase()} shops and stores. Find combo offers, 
              seasonal sales, and exclusive discounts all in one place.
            </p>
            <p>
              Compare prices, read details, and connect directly with sellers via WhatsApp. Whether you're 
              looking for new arrivals or clearance sales, discover amazing savings on {displayCategory.toLowerCase()} 
              products near you.
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};
